/**
 * @author jakubbujakowski
 * @since 2019-9-25
 */

import * as React from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router";

import { IS_DEV } from "@config/consts";
import { STEP_PROGRESS } from "@config/testIds/stepProgress";

import { TestWrapper } from "@ui/Atoms";
import { ITheme } from "@ui/themes/ThemeInterface";
import { rem, transition } from "@ui/helpers";
import { CheckCircle } from "@ui/Assets/Symbolicons/CheckCircle";
import { Number1 } from "@ui/Assets/Symbolicons/Number1";
import { Number2 } from "@ui/Assets/Symbolicons/Number2";
import { Number3 } from "@ui/Assets/Symbolicons/Number3";
import { Number4 } from "@ui/Assets/Symbolicons/Number4";
import { Number5 } from "@ui/Assets/Symbolicons/Number5";
import { Caret } from "@ui/Assets/Symbolicons/Caret";
import theme from "@ui/themes/default";

interface StepItem {
  label: string;
  route?: string;
  allowDirectNavigation?: boolean;
}

interface Props {
  activeStep: number;
  children?: React.ReactNode;
  className?: string;
  theme: ITheme;
  steps: StepItem[];
  labelFontSize?: "caption" | "body2";
  withNavigationButtonsOnMobile?: boolean;
  withoutNumbersOnMobile?: boolean;
  isMobile?: boolean;
  hidePrevBtn?: boolean;
}

const StepProgressComponent = (props: Props) => {
  const history = useHistory();
  const stepIcons = [Number1, Number2, Number3, Number4, Number5];

  const isPrevBtnVisible = props.activeStep > 1 && !props.hidePrevBtn;

  const handlePrevButtonClick = (): void => {
    history.push(String(props.steps[props.activeStep - 2].route));
  };

  const handleStepCircleClick = (step: StepItem): void => {
    console.log("step", step);
    history.push(step.route!);
  };

  return (
    <StepWNavigation>
      {props.withNavigationButtonsOnMobile && props.isMobile && (
        <SpaceKeeper>
          {isPrevBtnVisible && (
            <CaretWrapper onClick={handlePrevButtonClick} isPrev>
              <CaretStyled
                size={32}
                height={32}
                color={theme.colors.blue400}
                variant="line"
              />
            </CaretWrapper>
          )}
        </SpaceKeeper>
      )}
      <Steps
        className={props.className}
        withNavigationButtonsOnMobile={
          props.withNavigationButtonsOnMobile && props.isMobile
        }
      >
        {props.steps.map((step, idx) => {
          // when in god-mode navigate to any step as long as the route is defined
          const canClickAsDev = Boolean(step.route);
          // restrict navigation for normal users in prod
          const canClickAsUser =
            Boolean(step.route) &&
            step.allowDirectNavigation &&
            props.activeStep >= idx + 1;
          const allowClick = IS_DEV ? canClickAsDev : canClickAsUser;

          const isVisited = idx + 1 <= props.activeStep;
          const isCompleted = idx + 1 < props.activeStep;
          const StepIcon = stepIcons[idx];

          return (
            <TestWrapper
              testId={`${STEP_PROGRESS.ITEM} ${
                isVisited ? STEP_PROGRESS.ITEM_VISITED : ""
              } ${isCompleted ? STEP_PROGRESS.ITEM_COMPLETED : ""}`}
              key={`step-${idx}`}
            >
              <Step
                isCompleted={isCompleted}
                isVisited={isVisited}
                allowClick={allowClick}
                onClick={() => allowClick && handleStepCircleClick(step)}
              >
                {props.isMobile && props.withoutNumbersOnMobile ? (
                  <React.Fragment>
                    {isVisited ? (
                      <TestWrapper testId={STEP_PROGRESS.CHECK}>
                        <div className="step-circle">
                          <StepCircle isFilled />
                        </div>
                      </TestWrapper>
                    ) : (
                      <div className="step-circle">
                        <StepCircle />
                      </div>
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {isCompleted ? (
                      <TestWrapper testId={STEP_PROGRESS.CHECK}>
                        <div className="step-icon">
                          <CheckCircle
                            size={32}
                            height={32}
                            color={theme.colors.blue400}
                            variant="solid"
                          />
                        </div>
                      </TestWrapper>
                    ) : (
                      <div className="step-icon">
                        <StepIcon
                          size={32}
                          height={32}
                          color={
                            isVisited
                              ? theme.colors.blue400
                              : theme.colors.grey500
                          }
                          variant={isVisited ? "solid" : "line"}
                        />
                      </div>
                    )}
                  </React.Fragment>
                )}
                <StepLabel
                  isVisited={isVisited}
                  labelFontSize={props.labelFontSize}
                >
                  {step.label}
                </StepLabel>
              </Step>
            </TestWrapper>
          );
        })}
      </Steps>
    </StepWNavigation>
  );
};

const StepWNavigation = styled.div`
  display: flex;
  align-items: center;
`;

const CaretStyled = styled(Caret)``;

const SpaceKeeper = styled.div`
  width: 10%;
`;

const CaretWrapper = styled.div<{ isPrev?: boolean }>`
  cursor: pointer;

  ${CaretStyled} {
    transform: ${props => (props.isPrev ? "rotate(90deg)" : "rotate(-90deg)")};
  }
`;

const StepCircle = styled.div<{ isFilled?: boolean }>`
  display: block;
  position: relative;
  background: ${props =>
    props.isFilled ? props.theme.colors.blue400 : props.theme.colors.white};
  border: 1px solid
    ${props =>
      props.isFilled ? props.theme.colors.blue400 : props.theme.colors.grey500};
  border-radius: 50%;
  width: ${rem(12)};
  height: ${rem(12)};
  z-index: 1;
`;

const Steps = styled.ul<{ withNavigationButtonsOnMobile?: boolean }>`
  width: ${props => (props.withNavigationButtonsOnMobile ? "80%" : "100%")};

  ${props => props.theme.breakpoints.desktop} {
    width: 100%;
  }
`;

interface StepProps {
  isVisited: boolean;
  isCompleted: boolean;
  allowClick?: boolean;
}

const Step = styled.li<StepProps>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  text-align: center;
  flex-grow: 1;
  align-items: center;
  color: ${props => props.theme.colors.grey500};
  cursor: ${({ allowClick }) => (allowClick ? "pointer" : "auto")};

  .step-icon {
    width: ${rem(26)};
    height: ${rem(26)};
    margin-bottom: ${props => props.theme.margins.half};
    transition: ${transition(["background", "color", "border"])};
  }

  ${props =>
    props.isVisited &&
    css`
      &:not(:first-child) {
        .step-icon {
          &::before {
            background: ${props.theme.colors.blue400};
          }
        }
      }
    `}

  &:not(:first-child) {
    .step-icon {
      &::before {
        content: "";
        position: absolute;
        top: ${props => props.theme.margins.base_x2};
        right: calc(50% + ${rem(18)});
        height: 1px;
        width: calc(100% - ${rem(40)});
        background: ${props =>
          props.isVisited
            ? props.theme.colors.blue500
            : props.theme.colors.grey500};
      }
    }
  }

  // step circle without icon and used on mobile
  .step-circle {
    width: ${rem(12)};
    height: ${rem(12)};
    transition: ${transition(["background", "color", "border"])};
  }

  &:not(:first-child) {
    .step-circle {
      &::before {
        content: "";
        position: absolute;
        top: ${rem(6)};
        right: calc(50% + ${rem(6.4)});
        height: 1px;
        width: calc(100% - ${rem(11.2)});
        background: ${props =>
          props.isVisited
            ? props.theme.colors.blue500
            : props.theme.colors.grey500};
      }
    }
  }
`;

const StepLabel = styled.span<{ isVisited?: boolean; labelFontSize?: string }>`
  display: none;

  ${props => props.theme.breakpoints.tablet} {
    display: inline;
    font-size: ${props =>
      props.labelFontSize && props.theme.fonts.sizes[props.labelFontSize]};
    color: ${props =>
      props.isVisited
        ? props.theme.colors.blue400
        : props.theme.colors.grey500};
  }
`;

const StyledStepProgress = styled(StepProgressComponent)<Props>`
  display: flex;
  justify-content: space-between;
  list-style-type: none;
  padding-left: 0;
  margin-bottom: 0;
  margin: ${props =>
    props.withNavigationButtonsOnMobile &&
    props.isMobile &&
    `
    ${props.theme.margins.base_x1_5} 0
  `};

  li {
    flex-basis: 0;
    flex-grow: 1;
    margin-bottom: 0;
  }
`;

export const StepProgress = StyledStepProgress;
